import { nextSeasonalBoundary, parsePublishedSeasonalTheme, resolveSeasonalTheme, type PublishedSeasonalTheme, type SeasonalThemeDocument } from './seasonalTheme';

export interface SeasonalThemeSource {
  subscribe(onDocs: (docs: SeasonalThemeDocument[]) => void, onError: (error: unknown) => void): () => void;
}
export interface SeasonalThemeStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
}
export interface SeasonalThemeStoreState {
  themes: PublishedSeasonalTheme[];
  effective: PublishedSeasonalTheme | null;
  loaded: boolean;
  error: string | null;
}

type Deps = {
  source: SeasonalThemeSource;
  storage: SeasonalThemeStorage;
  onForeground(callback: () => void): () => void;
  preload(urls: string[]): Promise<boolean>;
  now?: () => number;
  setTimer?: (callback: () => void, ms: number) => unknown;
  clearTimer?: (timer: unknown) => void;
};

const CACHE_KEY = '@tonyte/seasonal-themes-v1';
const MAX_TIMER = 2 ** 31 - 1;

export function createSeasonalThemeStore(deps: Deps) {
  const now = deps.now ?? Date.now;
  const setTimer = deps.setTimer ?? ((callback, ms) => setTimeout(callback, ms));
  const clearTimer = deps.clearTimer ?? (timer => clearTimeout(timer as ReturnType<typeof setTimeout>));
  let state: SeasonalThemeStoreState = { themes: [], effective: null, loaded: false, error: null };
  const listeners = new Set<(state: SeasonalThemeStoreState) => void>();
  let stopSource: (() => void) | null = null;
  let stopForeground: (() => void) | null = null;
  let timer: unknown = null;
  let generation = 0;

  const emit = (patch: Partial<SeasonalThemeStoreState>) => {
    state = { ...state, ...patch };
    listeners.forEach(listener => listener(state));
  };
  const schedule = () => {
    if (timer) clearTimer(timer);
    timer = null;
    const boundary = nextSeasonalBoundary(state.themes, now());
    if (boundary === null) return;
    timer = setTimer(() => { timer = null; void activate(state.themes); }, Math.min(MAX_TIMER, Math.max(0, boundary - now()) + 250));
  };
  const activate = async (themes: PublishedSeasonalTheme[]) => {
    const token = ++generation;
    const candidate = resolveSeasonalTheme(themes, now());
    const urls = candidate ? [...new Set(Object.values(candidate.light.assets ?? {}).concat(Object.values(candidate.dark.assets ?? {})).filter(Boolean) as string[])] : [];
    const ready = !urls.length || await deps.preload(urls).catch(() => false);
    if (token !== generation) return;
    emit({ themes, effective: ready ? candidate : null, loaded: true, error: ready ? null : 'Seasonal artwork could not be loaded.' });
    schedule();
  };
  const handleDocs = (docs: SeasonalThemeDocument[]) => {
    const themes = docs.map(parsePublishedSeasonalTheme).filter((value): value is PublishedSeasonalTheme => Boolean(value));
    deps.storage.setItem(CACHE_KEY, JSON.stringify(docs.map(doc => ({ id: doc.id, data: plain(doc.data) })))).catch(() => undefined);
    void activate(themes);
  };
  const start = () => {
    if (stopSource) return;
    stopSource = deps.source.subscribe(handleDocs, error => emit({ loaded: true, error: String(error) }));
  };
  const hydrate = async () => {
    try {
      const raw = await deps.storage.getItem(CACHE_KEY);
      if (!raw) return;
      const docs = JSON.parse(raw) as SeasonalThemeDocument[];
      if (Array.isArray(docs)) await activate(docs.map(parsePublishedSeasonalTheme).filter((v): v is PublishedSeasonalTheme => Boolean(v)));
    } catch { /* invalid cache is ignored */ }
  };
  return {
    getState: () => state,
    subscribe(listener: (state: SeasonalThemeStoreState) => void) { listeners.add(listener); return () => listeners.delete(listener); },
    start() {
      void hydrate();
      start();
      stopForeground = deps.onForeground(() => { start(); void activate(state.themes); });
    },
    stop() {
      stopSource?.(); stopSource = null;
      stopForeground?.(); stopForeground = null;
      if (timer) clearTimer(timer);
      timer = null;
    },
  };
}

function plain(value: unknown): unknown {
  if (value && typeof value === 'object') {
    const timestamp = value as { toMillis?: () => number };
    if (typeof timestamp.toMillis === 'function') return timestamp.toMillis();
    if (Array.isArray(value)) return value.map(plain);
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, child]) => [key, plain(child)]));
  }
  return value;
}

